import { useGetClassificationsQuery } from '@/services/productCatalog';
import { useGetWarrantyProvidersQuery } from '@/services/warranties';
import { Box, Button, FormikSelectInputNative, FormikTextInput, Modal } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikValues } from 'formik';
import { SyntheticEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, warrantyMap, WarrantyPaymentFrequency, WarrantyType } from 'types';

export const EquipmentWarrantyModal = ({
  isModalOpen,
  closeModal,
  warrantyType,
  onSubmit,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  warrantyType?: string;
  onSubmit: (values: FormikValues) => void;
}) => {
  const { programType = ProgramType.hvac } = useParams<{ programType: string }>();
  const warranty = warrantyMap[programType as ProgramType][warrantyType as WarrantyType];
  const { data: classificationData = [] } = useGetClassificationsQuery({ path: 'hvac/equipment-materials' });
  const equipment = useMemo(() => {
    const parentClassificationIndex = classificationData.findIndex(
      (classification) => classification.slug === 'equipment-materials',
    );
    const ductlessIndoorIndex = classificationData.findIndex(
      (classification) => classification.slug === 'ductless-indoor',
    );
    const ductlessOutdoorIndex = classificationData.findIndex(
      (classification) => classification.slug === 'ductless-outdoor',
    );
    const classifications = [...classificationData];
    classifications.splice(parentClassificationIndex, 1);
    classifications.splice(ductlessIndoorIndex, 1);
    classifications.splice(ductlessOutdoorIndex, 1);
    return classifications.map((classification) => ({
      name: classification.name,
      type: classification.lightReachProductType,
    }));
  }, [classificationData]);

  const { data: warrantyProviderResponse } = useGetWarrantyProvidersQuery({});

  const warrantyProviderOptions = useMemo(() => {
    const programTypeProviders = warrantyProviderResponse?.data?.find(
      (item) => item.programType === programType,
    )?.providers;

    const warrantyProviderList = programTypeProviders?.map((p) => p.name) || [];
    return warrantyProviderList.map((element) => {
      return {
        label: element,
        value: element,
      };
    });
  }, [programType, warrantyProviderResponse]);

  return (
    <Modal ariaLabeledBy="createEquipmentWarranty" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createEquipmentWarranty" title={`Add ${warranty}`} onDismiss={closeModal} />
      <Formik
        initialValues={{
          warrantyProvider: '',
          paymentFrequency: WarrantyPaymentFrequency.upfront,
        }}
        onSubmit={({ warrantyProvider, paymentFrequency, ...rest }) =>
          onSubmit({
            warrantyProvider,
            paymentFrequency,
            warrantyType: WarrantyType.equipment,
            equipment: { ...rest },
          })
        }
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <Modal.Body overflow={'auto'} maxHeight={'70vh'}>
                <Box direction="column" gap="md">
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Warranty Provider"
                    name="warrantyProvider"
                    id="warrantyProvider"
                    options={warrantyProviderOptions}
                    isRequired
                  />
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Payment Frequency"
                    name="paymentFrequency"
                    id="paymentFrequency"
                    options={Object.keys(WarrantyPaymentFrequency).map((key) => ({
                      label: WarrantyPaymentFrequency[key as keyof typeof WarrantyPaymentFrequency],
                      value: key,
                    }))}
                    isRequired
                  />
                  {equipment.map((item) => (
                    <Field
                      key={item.type}
                      type="number"
                      component={FormikTextInput}
                      label={item.name}
                      name={item.type}
                      id={item.type}
                      isRequired
                    />
                  ))}
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
